export const findPropertiesRegex = new RegExp(/\$\{([^\}]+)\}/g);

/*
  parseTemplatedKeysToString is a util function that helps us to parse a string with templated keys and with other characters to a string with the values of the keys.
  For example:
    An AccountDTO is an object with the following structure:
      {
        accountName: string,
        entityAccountId: string,
      }

  Then, let's say we will want to use the custom entities fetcher for accounts and we want to combine those two fields in a single string for each option.
  For example (final requested result): 'accountName (entityAccountId)'.
  We can use the following template as an array of string: ['${accountName}', '(${entityAccountId})'].
  Then, we can use the parseTemplatedKeysToString function to parse the template to a string with the values of the keys.
  Example result: parseTemplatedKeysToString(['${accountName}', '(${entityAccountId})'], accountDTO) => 'accountName (entityAccountId)'.

  If one of the elements in the templateKeys array is null in the sourceObject, it will be filtered from the final string result (to avoid a case like: 'accountName (null)').

  You can use whatever separator you want between the keys, by default it will be a space (' ').
  You can use whatever template you like, as long as it matches the findPropertiesRegex regex (i.e. - the key templated like this: '${key}').
  You can use a key that is a nested object (i.e. - for 'accountDTO.createAudit.userName', the value will be of the username key in the createAudit object in the accountDTO object)
  You can use whatever sourceObject you want, as long as it has the keys that are used in the template. if sourceObject won't have the keys (and value !== null) that are used in the template, it will be filtered from the final string result.
  You can use however many keys you want in the template, as long as they fit the rules above.

  More examples:
    parseTemplatedKeysToString(['${accountName}', '${entityAccountId}'], accountDTO, '-') => 'accountName-entityAccountId'
    parseTemplatedKeysToString(['${accountName}', '/${entityAccountId}'], accountDTO, '') => 'accountName/entityAccountId'
    parseTemplatedKeysToString(['${accountName}', '@${entityAccountId}', '.${accountId}'], accountDTO, '') => 'accountName@entityAccountId.accountId'

  Note: If you want to use the function for custom entities fetcher, please make sure you're adding to the ENTITIES: Array<EntitiesMetadataTypes> the relevant entity metadata too, in the same template you used in the custom entity fetcher, otherwise the option label and the actual result of the selected option will be different.
*/

export function parseTemplatedKeysToString(
  templateKeys: Array<string>,
  sourceObject: any,
  keysSeparator: string = ' '
): string {
  return templateKeys
    .map((templateKey) => parseKey(templateKey, sourceObject))
    .filter((key) => key)
    .join(keysSeparator);
}

function parseKey(templateKey: string, sourceObject: any): string {
  let originalKey = null;
  const replacer = (match: string, sourceObjectKey: string): string => {
    originalKey = sourceObjectKey;
    return sourceObjectKey.split('.').reduce((accumulator, currentValue) => accumulator[currentValue], sourceObject);
  };

  const parsedKey = templateKey.replace(findPropertiesRegex, replacer);
  if (!sourceObject[originalKey]) return;
  return parsedKey;
}
